<i18n src="@/locales/modules/RequestTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		:class="[
			'custom-dialog request-form-dialog',
			{
				'custom-dialog--edit-mode': !isCreateMode,
				'no-border-bottom-header': isNoBorderBottomHeader
			}
		]"
		:visible.sync="isDialogActiveLocal"
		:before-close="handleBeforeCloseDialog"
		@close="handleOnDialogClose"
	>
		<div slot="title" class="flex justify-between items-center w-full">
			<span class="el-dialog__title break-normal">{{ formTitle }}</span>
			<request-context-menu
				v-if="!isCreateMode"
				displayMode="detail"
				:request="localValue"
				@onClickCancel="handleClickCancelRequest"
				@onClickCreateTodo="handleClickCreateTodo"
				@onClickDuplicate="handleClickDuplicateMenu"
			/>
		</div>

		<cbs-comment-with-toggle
			:postId="localValue && localValue.id"
			:model="$constant.RELATION_MODELS.REQUEST"
			@ps-scroll-down="isNoBorderBottomHeader = false"
			@ps-y-reach-start="isNoBorderBottomHeader = true"
		>
			<div class="px-6">
				<!-- REQUEST TYPE -->
				<div class="my-5" v-if="isCreateMode">
					<label>
						{{ $t("RequestType") }}
						<span class="text-danger">*</span>:
					</label>

					<v-select
						label="text"
						ref="requestTypesSelectRef"
						:options="parsedRequestTypes"
						:value="localSelectedRequestType"
						:getOptionKey="option => option.value"
						:placeholder="`${$t('Actions.Choose')} ${$t('RequestType').toLowerCase()}`"
						@input="handleChangeRequestType"
						@search:focus="handleOnFocusSelectRequestTypes"
					>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
					<input class="hidden" name="request-type" v-validate="'required'" v-model="localSelectedRequestType" :data-vv-as="$t('RequestType')" />
					<span class="text-danger text-sm" v-show="errors.has('request-type')">{{ errors.first("request-type") }}</span>
				</div>

				<request-steps class="-mx-6" :value="stepsData" :activeId="activeStepId" :disabledIds="disabledStepIds" @onClick="handleSetActiveStep" />

				<request-step-info class="mt-1 mb-5" v-if="!isCreateMode" :value="currentStepInfo" :title="$t('FormStepInfo')" />

				<h4 class="text-black mt-1 mb-3 font-bold uppercase text-lg" v-if="isFirstStep || isActiveFormDataHasControl">{{ $t("Content") }}</h4>

				<div v-if="isCreateMode || isActiveFormCanUpdate">
					<!-- REQUEST PURPOSE -->
					<div class="mt-5" v-if="isFirstStep && localValue.req_type_id">
						<label for class="vs-input--label">{{ $t("Purpose") }}<span class="text-danger">&nbsp;*</span>:</label>
						<cbs-input
							class="w-full"
							data-vv-delay="1"
							name="request-purpose"
							v-validate="'required'"
							v-model="localValue.request_reason"
							:data-vv-as="$t('Purpose')"
							:placeholder="`${$t('Actions.Input')} ${$t('Purpose').toLowerCase()}`"
						/>
						<span class="text-danger text-sm" v-show="errors.has('request-purpose')">{{ errors.first("request-purpose") }}</span>
					</div>

					<cbs-dynamic-form
						v-if="isActiveFormDataHasControl"
						v-model="localValue.activeFormData.controls"
						:mappedAttachObj="mappedAttachObj"
						:key="`${localValue.activeFormData.id}-${localValue.updated_at}`"
						@onSetInitData="handleSetInitFormData"
						@onChangeMappedAttachObj="handleSetMappedAttachObj"
					/>
				</div>

				<div v-else>
					<div class="flex mb-1" v-if="(isFirstStep || isLastStep) && localValue.request_reason">
						<label class="w-1/2 sm:w-1/4 text-gray-1 break-normal">{{ $t("Purpose") }}:</label>
						<div class="text-black w-1/2 sm:w-3/4">{{ localValue.request_reason }}</div>
					</div>
					<dynamic-form-content v-if="isActiveFormDataHasControl" :mappedAttachObj="mappedAttachObj" :value="localValue.activeFormData.controls" />
				</div>

				<div class="dynamic-request my-6" v-if="isCanConfigNotificationSteps">
					<div class="dynamic-request__divider py-3 -mx-6 my-6"></div>
					<div
						v-if="!isShowConfigNotificationSteps && !isFirstStep"
						class="dynamic-request__btn-add-config px-3 text-lg text-gray-2 rounded border border-dashed border-gray-4 py-1 w-full text-center cursor-pointer hover:border-gray-3 hover:font-bold"
						@click="isShowConfigNotificationSteps = true"
					>
						{{ $t("AddNotificationStepsConfig") }}
					</div>

					<transition name="fade">
						<request-notification-steps-config
							v-if="isShowConfigNotificationSteps || isFirstStep"
							class="dynamic-request__config-form mt-6 mb-5"
							ref="dynamicStepConfigRef"
							v-model="dynamicStepConfig"
							:isRequiredName="isFirstStep"
							:isRequiredConfig="isFirstStep"
							:isRequiredIfAny="!isFirstStep"
							:key="localValue.activeFormData.id"
							:isEditable="isCanConfigNotificationSteps"
						/>
					</transition>
					<p class="text-black italic leading-tight" v-if="!isFirstStep && isShowConfigNotificationSteps">{{ $t("DynamicRequestHint") }}</p>
				</div>
			</div>
		</cbs-comment-with-toggle>

		<div
			slot="footer"
			v-if="isDialogActiveLocal && isHasFooter"
			:class="['flex items-center flex-wrap sm:flex-no-wrap', isCanExport ? 'justify-end' : isActiveFormCanApproveOrReject ? 'sm:px-12 justify-between' : 'justify-center']"
		>
			<vs-button
				color="danger"
				class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold"
				@click.stop="() => handleClickApproveOrReject(false)"
				v-if="isActiveFormCanReject"
				>{{ $t("Actions.Reject") }}
			</vs-button>

			<vs-button
				color="success"
				class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold"
				@click.stop="() => handleClickApproveOrReject(true)"
				v-if="isActiveFormCanApproveOrReject"
				>{{ $t("Actions.Agree") }}
			</vs-button>

			<vs-button
				v-if="isCreateMode || (isActiveFormCanUpdate && isActiveFormDataHasControl && !isDynamicRequest) || (isDynamicRequest && isFirstStep)"
				:disabled="!localValue.req_type_id"
				class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold"
				@click.stop="submitData"
				>{{ $t(`${isCreateMode ? "Actions.Send" : "Actions.Save"}`) }}
			</vs-button>


			<vs-button type="border" v-if="isRequestRecreateable" class="w-full sm:w-2/5 mb-3 sm:mb-0 sm:mr-6 last:mr-0 last:mb-0 font-bold" @click.stop="handleRecreateRequest"
				>{{ $t("Actions.Recreate") }}
			</vs-button>

			<cbs-download-button v-if="isCanExport" :btnText="$t('Actions.Download')" @click="handleExportData" />
		</div>
	</el-dialog>
</template>

<script>
import { get as lodashGet, cloneDeep as lodashCloneDeep, isEmpty as lodashIsEmpty } from "lodash";

import vSelect from "vue-select";
import RequestSteps from "./RequestSteps";
import RequestStepInfo from "./RequestStepInfo";
import RequestContextMenu from "./RequestContextMenu";
import CbsDynamicForm from "@/components/cbs-dynamic-form/CbsDynamicForm";
import DynamicFormContent from "@/components/cbs-dynamic-form/DynamicFormContent";
import CbsCommentWithToggle from "@/components/cbs-comment-with-toggle/CbsCommentWithToggle";
import RequestNotificationStepsConfig from "@/components/cbs-request-notification-steps-config/RequestNotificationStepsConfig";

const initDynamicStepConfig = {
	name: "",
	config: {
		approversConfig: {
			content: "",
			relations: []
		},
		approvedNotificationConfig: {
			content: "",
			relations: []
		},
		rejectedNotificationConfig: {
			content: "",
			relations: []
		}
	}
};

export default {
	name: "RequestFormDialog",
	provide() {
		return { parentValidator: this.$validator };
	},
	components: {
		vSelect,
		RequestSteps,
		CbsDynamicForm,
		RequestStepInfo,
		RequestContextMenu,
		DynamicFormContent,
		CbsCommentWithToggle,
		RequestNotificationStepsConfig
	},
	props: {
		value: {
			type: Object,
			required: true,
			validator: prop => {
				if (lodashIsEmpty(prop)) {
					return true;
				}
				return prop.hasOwnProperty("req_type_id") && prop.hasOwnProperty("activeFormData");
			}
		},
		relationsData: Array,
		isDialogActive: Boolean,
		requestTypes: {
			type: Array,
			required: true
		},
		stepsData: {
			type: Array,
			required: true
		},
		activeStepId: {
			type: [Number, String]
		}
	},
	emits: {
		closeDialog: {},
		onClickSubmit: {},
		onDialogClose: {},
		onClickStepItem: {},
		onClickDuplicate: {},
		onClickCreateTodo: {},
		onClickExportData: {},
		onChangeRequestType: {},
		onIsFormDirtyChange: {},
		onClickCancelRequest: {},
		onClickRecreateRequest: {},
		onClickApproveOrReject: {},
		onFocusSelectRequestTypes: {}
	},
	data() {
		return {
			initFormData: {},
			isFormDirty: false,
			initStepIndex: null,
			requestPurpose: null,
			mappedAttachObj: {},
			isNoBorderBottomHeader: true,

			isShowConfigNotificationSteps: false,
			dynamicStepConfig: lodashCloneDeep(initDynamicStepConfig)
		};
	},
	computed: {
		localValue: {
			get() {
				return this.value;
			},
			set(val) {
				this.$emit("input", val);
			}
		},
		isCreateMode() {
			return !this.localValue.id;
		},
		isDialogActiveLocal: {
			get() {
				return this.isDialogActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeDialog");
				}
			}
		},
		formTitle() {
			return this.isCreateMode ? this.$t("AddNewRequest") : `${this.$t("DetailRequest")} - ${lodashGet(this.localValue, "reqtype.name") || ""}`;
		},
		parsedRequestTypes() {
			return (
				this.requestTypes
					.filter(requestType => !!requestType.can_create)
					.map(requestType => ({
						value: requestType.id,
						text: requestType.name,
						statuses: requestType.statuses
					})) || []
			);
		},
		disabledStepIds() {
			if (this.isRequestCompleted) {
				return [];
			} else if (this.initStepIndex !== -1) {
				return this.stepsData.filter((step, index) => index > this.initStepIndex).map(step => step.id);
			}
			return undefined;
		},
		localSelectedRequestType() {
			return this.parsedRequestTypes.find(requestType => requestType.value === this.localValue.req_type_id) || null;
		},
		isFirstStep() {
			return this.stepsData.findIndex(step => step.id === this.activeStepId) === 0;
		},
		isLastStep() {
			return this.stepsData.findIndex(step => step.id === this.activeStepId) === this.stepsData.length - 1;
		},
		isRequestCanceled() {
			return !!this.localValue && this.localValue.is_cancel;
		},
		isRequestCompleted() {
			return this.initStepIndex === this.stepsData.length - 1 || (this.stepsData[this.stepsData.length - 1] && this.stepsData[this.stepsData.length - 1].status === 1);
		},
		isCanExport() {
			return this.isRequestCompleted && this.localValue && this.localValue.can_export;
		},
		isRequestCancelable() {
			return this.localValue && this.localValue.can_cancel;
		},
		isRequestRecreateable() {
			return (
				!this.isCreateMode &&
				this.localValue &&
				this.localValue.is_owner &&
				this.localValue.reqtype &&
				!this.localValue.reqtype.disable &&
				(this.localValue.is_reject || this.isRequestCanceled)
			);
		},
		isHasFooter() {
			return this.isCanExport || this.isCreateMode || this.isRequestRecreateable || (this.isActiveFormCanUpdate && this.isActiveFormDataHasControl) || this.isActiveFormCanApproveOrReject;
		},
		isActiveFormDataHasControl() {
			return !!(this.localValue.activeFormData && this.localValue.activeFormData.controls && this.localValue.activeFormData.controls.length);
		},
		isActiveFormCanApproveOrReject() {
			let ok = !this.isCreateMode && !this.isFirstStep && this.localValue.activeFormData.status === 0 && this.localValue.can_edit;

			let response = this.localValue;
			if (response.reqstatus && response.reqstatus.approverFillInfo) {
				if (response.data) {
					let dataObj = JSON.parse(response.data);
					if (dataObj) {
						if (!dataObj.hasOwnProperty("child")) {
							ok = false;
						}
					}
				}
			}

			return ok;
		},
		isActiveFormCanReject() {
			return !this.isCreateMode && !this.isFirstStep && this.localValue.activeFormData.status === 0 && this.localValue.can_edit;
		},
		isActiveFormCanUpdate() {
			return this.localValue.activeFormData && this.localValue.activeFormData.canUpdate;
		},
		isDynamicRequest() {
			return (
				(this.localValue && !this.localValue.id && this.localSelectedRequestType && this.localSelectedRequestType.statuses && lodashIsEmpty(this.localSelectedRequestType.statuses)) ||
				(this.localValue && this.localValue.id && this.localValue.is_dynamic)
			);
		},
		isCanConfigNotificationSteps() {
			return this.isDynamicRequest && (this.isActiveFormCanApproveOrReject || (!this.localValue.id && this.isFirstStep && !lodashIsEmpty(this.localSelectedRequestType)));
		},
		isHasApproversConfigContentValue() {
			return !!this.dynamicStepConfig.config.approversConfig.content;
		},
		isHasApproversConfigTargetValue() {
			return this.dynamicStepConfig.config.approversConfig.relations && this.dynamicStepConfig.config.approversConfig.relations.length > 0;
		},
		isHasApprovedNotificationConfigContentValue() {
			return !!this.dynamicStepConfig.config.approvedNotificationConfig.content;
		},
		isHasApprovedNotificationConfigTargetValue() {
			return this.dynamicStepConfig.config.approvedNotificationConfig.relations && this.dynamicStepConfig.config.approvedNotificationConfig.relations.length > 0;
		},
		isHasRejectedNotificationConfigContentValue() {
			return !!this.dynamicStepConfig.config.rejectedNotificationConfig.content;
		},
		isHasRejectedNotificationConfigTargetValue() {
			return this.dynamicStepConfig.config.rejectedNotificationConfig.relations && this.dynamicStepConfig.config.rejectedNotificationConfig.relations.length > 0;
		},
		isHasNotificationConfigValue() {
			return (
				this.isHasApproversConfigTargetValue ||
				this.isHasApproversConfigContentValue ||
				this.isHasApprovedNotificationConfigTargetValue ||
				this.isHasApprovedNotificationConfigContentValue ||
				this.isHasRejectedNotificationConfigContentValue ||
				this.isHasRejectedNotificationConfigTargetValue
			);
		},
		isHasNotificationStepNameValue() {
			return !!this.dynamicStepConfig.name;
		},
		computedFormData() {
			const trackingData = {
				localValue: lodashCloneDeep(this.localValue),
				initFormData: lodashCloneDeep(this.initFormData)
			};
			if (this.isDynamicRequest) {
				trackingData.dynamicStepConfig = lodashCloneDeep(this.dynamicStepConfig);
			}
			return trackingData;
		},
		mappedStepStatuses() {
			return {
				[this.$constant.REQUEST_STATUSES.WAITING]: "Waiting",
				[this.$constant.REQUEST_STATUSES.CANCELED]: "Canceled",
				[this.$constant.REQUEST_STATUSES.APPROVED]: "Approved",
				[this.$constant.REQUEST_STATUSES.REJECTED]: "Rejected"
			};
		},
		currentStepInfo() {
			const activeStepIndex = this.stepsData.findIndex(step => step.id === this.activeStepId);
			const status = this.localValue.activeFormData.status || 0;
			const requestCode = lodashGet(this.localValue, "code");
			const requestTypeName = lodashGet(this.localValue, "reqtype.name");
			const creatorName = lodashGet(this.localValue, "requester.name");
			const departmentName = lodashGet(this.localValue, "requester.department.name");
			const createdDate = lodashGet(this.localValue, "created_at");
			let updatedDate = lodashGet(this.localValue, "updated_at");
			let statusName = lodashGet(this.localValue, "reqstatus.name");
			let approveData = [];

			if (this.localValue.status === this.$constant.REQUEST_STATUSES.APPROVED) {
				statusName = this.$t("Complete");
			}
			if (this.localValue.status === this.$constant.REQUEST_STATUSES.CANCELED) {
				statusName = this.$t("Canceled");
			}

			// in between first step and last step
			if (activeStepIndex > 0 && activeStepIndex < this.stepsData.length - 1) {
				const activeStepData = (lodashGet(this.localValue, "reqtype.statuses") || []).find(step => step.id === this.localValue.activeFormData.id) || {};
				statusName = this.$t(this.mappedStepStatuses[status]);
				const description = activeStepData.description ? activeStepData.description : "";
				const approverFillInfo = activeStepData.approverFillInfo ? activeStepData.approverFillInfo : "";
				const isPendingInput = !!activeStepData.pending_input;
				if (!lodashIsEmpty(activeStepData.logs)) {
					approveData.push({
						note: lodashGet(activeStepData, "logs.note"),
						updatedAt: lodashGet(activeStepData, "logs.updated_at"),
						approverName: lodashGet(activeStepData, "logs.approved_by.name")
					});

					return {
						status,
						statusName,
						updatedDate,
						approveData,
						isPendingInput,
						description,
						approverFillInfo
					};
				}

				return {
					status,
					statusName,
					isPendingInput
				};
			}

			// Completed Step
			if (activeStepIndex === this.stepsData.length - 1) {
				approveData = (lodashGet(this.localValue, "reqtype.statuses") || [])
					.filter(formStep => !lodashIsEmpty(formStep.logs))
					.map(formStep => ({
						note: lodashGet(formStep, "logs.note"),
						stepName: lodashGet(formStep, "name"),
						updatedAt: lodashGet(formStep, "logs.updated_at"),
						approverName: lodashGet(formStep, "logs.approved_by.name")
					}));

				return {
					status,
					statusName,
					creatorName,
					departmentName,
					createdDate,
					updatedDate,
					approveData,
					requestCode,
					requestTypeName
				};
			}

			// First Step
			return {
				status,
				statusName,
				creatorName,
				departmentName,
				createdDate,
				requestCode,
				requestTypeName,
				isCancel: this.localValue && this.localValue.is_cancel,
				relations: this.localValue && this.localValue.relations ? this.localValue.relations : null,
				cancelReason: status === this.$constant.REQUEST_STATUSES.CANCELED ? this.localValue.cancel_reason : null
			};
		},
		isRequestReasonChanged() {
			let initRequestReason = "";
			let newRequestReason = "";
			if (typeof this.localValue.request_reason !== "undefined") {
				newRequestReason = this.localValue.request_reason;
			}
			if (typeof this.initFormData.request_reason !== "undefined") {
				initRequestReason = this.initFormData.request_reason;
			}
			return newRequestReason !== initRequestReason;
		},
		isRequestFormDataChanged() {
			if (!lodashIsEmpty(this.localValue.activeFormData) && !lodashIsEmpty(this.initFormData.activeFormData)) {
				// not compare calculator control value because of auto calulation with empty value will result in 0 => filtered calculator control
				const filteredLocalValueControls = this.localValue.activeFormData.controls.filter(field => field.control !== this.$constant.request.control.calculator);
				const filteredInitValueControls = this.initFormData.activeFormData.controls.filter(field => field.control !== this.$constant.request.control.calculator);
				return JSON.stringify(filteredLocalValueControls) !== JSON.stringify(filteredInitValueControls);
			}
			return false;
		}
	},
	watch: {
		"localValue.activeFormData.id"() {
			this.initFormData = lodashCloneDeep(this.localValue);
			this.dynamicStepConfig = lodashCloneDeep(initDynamicStepConfig);
		},
		activeStepId() {
			// update initStepIndex in dynamic request case
			const activeStepIndex = this.stepsData.findIndex(step => step.id === this.activeStepId);
			if (this.isDynamicRequest && activeStepIndex > this.initStepIndex) {
				this.initStepIndex = activeStepIndex;
			}
		},
		computedFormData: {
			handler: function() {
				this.isFormDirty =
					this.isRequestReasonChanged ||
					this.isHasNotificationStepNameValue ||
					(this.isDynamicRequest && this.isHasNotificationConfigValue) ||
					(this.isActiveFormCanUpdate && this.isRequestFormDataChanged);
			},
			deep: true
		},
		isFormDirty() {
			this.$emit("onIsFormDirtyChange", this.isFormDirty);
		}
	},
	methods: {
		handleBeforeCloseDialog(setDialogHide) {
			this.handleCloseDialog(setDialogHide, this.isFormDirty);
		},
		handleClickCreateTodo(data) {
			this.$emit("onClickCreateTodo", data);
		},
		handleClickCancelRequest(data) {
			this.$emit("onClickCancelRequest", data);
		},
		handleChangeRequestType(option) {
			if (this.localValue.req_type_id && this.isRequestFormDataChanged) {
				this.$vs.dialog({
					type: "confirm",
					color: "danger",
					title: this.$t("Actions.Confirm"),
					text: this.$t("ChangeRequestTypeConfirm"),
					acceptText: this.$t("Actions.Agree"),
					cancelText: this.$t("Actions.Cancel"),
					accept: () => this.$emit("onChangeRequestType", option)
				});
			} else {
				this.$emit("onChangeRequestType", option);
			}
		},
		isAllowToSwitchTab() {
			return new Promise(resolve => {
				if (this.isFormDirty) {
					this.$vs.dialog({
						type: "confirm",
						color: "danger",
						title: this.$t("Actions.Confirm"),
						text: this.$t("SwitchTabConfirm"),
						acceptText: this.$t("Actions.Agree"),
						cancelText: this.$t("Actions.Cancel"),
						accept: () => resolve(true),
						cancel: () => resolve(false)
					});
				} else {
					resolve(true);
				}
			});
		},
		handleSetActiveStep(itemClicked) {
			if (this.activeStepId && this.activeStepId !== itemClicked.id) {
				this.isAllowToSwitchTab().then(isAllow => {
					if (isAllow) {
						this.$emit("onClickStepItem", itemClicked, this.initFormData);
					}
				});
			}
		},
		handleOnDialogClose() {
			this.$emit("onDialogClose");
		},
		handleSetMappedAttachObj(newMappedAttachObj) {
			this.mappedAttachObj = newMappedAttachObj;
		},
		handleSetInitFormData(controlsFormData) {
			this.initFormData.activeFormData = {
				...this.initFormData.activeFormData,
				controls: lodashCloneDeep(controlsFormData)
			};
		},
		handleExportData() {
			this.$emit("onClickExportData");
		},
		handleClickApproveOrReject(isApprove) {
			if (isApprove) {
				this.$validator.validateAll().then(result => {
					if (result) {
						if (this.isHasNotificationConfigValue && this.isHasNotificationStepNameValue) {
							this.$emit("onClickApproveOrReject", this.localValue.id, isApprove, this.dynamicStepConfig);
						} else {
							this.$emit("onClickApproveOrReject", this.localValue.id, isApprove);
						}
					} else {
						this.setActiveClosestErrorNotificationStepsConfigTab();
					}
				});
			} else {
				this.$emit("onClickApproveOrReject", this.localValue.id, isApprove);
			}
		},
		setActiveClosestErrorNotificationStepsConfigTab() {
			if (this.isCanConfigNotificationSteps) {
				const listTabNames = ["approverConfig", "approvedNotificationConfig", "rejectedNotificationConfig"];
				let listErrorFields = lodashGet(this.$validator, "errors.items") || [];
				if (listErrorFields && listErrorFields.length) {
					listErrorFields = listErrorFields.some(errorField => {
						const splitErrorFormName = errorField.field.split("-")[0];
						const index = listTabNames.findIndex(val => val === splitErrorFormName);
						if (index !== -1 && this.$refs.dynamicStepConfigRef && this.$refs.dynamicStepConfigRef.$children && this.$refs.dynamicStepConfigRef.$children[1]) {
							this.$refs.dynamicStepConfigRef.$children[1]._data.activeIndex = index;
							return true;
						}
					});
				}
			}
		},
		handleRecreateRequest() {
			this.$emit("onClickRecreateRequest", this.localValue);
		},
		async submitData() {
			this.$validator.validateAll().then(async result => {
				if (result) {
					await new Promise((resolve, reject) => this.$emit("onClickSubmit", this.localValue, this.dynamicStepConfig, this.relationsData, resolve, reject));
					this.initFormData = lodashCloneDeep(this.localValue);
					this.$helper.notifySuccess({
						context: this,
						options: {
							text: this.$t(`${this.localValue.id ? "UpdateRequestSuccessful" : "CreateRequestSuccessful"}`)
						}
					});
				} else {
					this.setActiveClosestErrorNotificationStepsConfigTab();
				}
			});
		},
		handleOnFocusSelectRequestTypes() {
			const refLoading = this.$refs.requestTypesSelectRef && this.$refs.requestTypesSelectRef.toggleLoading;
			this.$emit("onFocusSelectRequestTypes", refLoading);
		},
		handleClickDuplicateMenu(data) {
			this.$emit("onClickDuplicate", data);
		}
	},
	created() {
		this.initFormData = lodashCloneDeep(this.localValue);
		this.initStepIndex = this.isCreateMode ? 0 : this.stepsData.findIndex(step => step.id === this.activeStepId);
		const localAttachsValue = this.localValue.attachs || [];
		localAttachsValue.forEach(attach => (this.mappedAttachObj[attach.id] = attach));
	}
};
</script>

<style lang="scss" scoped>
.custom-dialog--edit-mode {
	&.no-border-bottom-header {
		::v-deep .el-dialog__header {
			border-bottom: 0;
		}
	}
}

.request-form-dialog {
	.dynamic-request__divider {
		background-color: #f5f5f5;
	}
}
</style>
